// eslint-disable-next-line
import React, { useState, useEffect, useCallback } from 'react';
import { Field, Form, FieldProps, FormikContextType, connect } from 'formik';
import Grid from '@material-ui/core/Grid';
import { Entity, ChannelType } from 'core/entities';
import { Select, SubmitButton, GoBackButton } from 'components/forms';
import { makeStyles } from '@material-ui/core/styles';
import { AuthActions, AuthStateAgentInfo } from 'store/modules/auth';
import { Nullable } from 'utils/types';
import { useAppDispatch } from 'store';
import styled from 'styled-components';
import { spacing } from '@material-ui/system';
import dictionary from 'utils/dictionary';
import { SellerInfo } from './entity.model';
import AgentDocumentNumberField from './AgentDocumentNumberField';
import EntityIconLabel from './EntityIconLabel';

const GridBox = styled(Grid)`
  ${spacing}
`;

const useStyles = makeStyles(() => ({
  entityButtons: {
    marginTop: '6rem',
  },
  docNumber: {
    width: '100%',
    marginTop: '2rem',
    marginBottom: '-2.5rem',
  },
  docNumberDialog: {
    width: '100%',
    marginTop: '2rem',
    marginBottom: '1.5rem',
  },
}));

type Props = {
  data: Entity[];
  gridClass: string;
  showButtons?: boolean;
  isModalDialog?: boolean;
  handleBack?: () => void;
  handleEntitySelectChange: (value) => void;
  sellerInfo?: AuthStateAgentInfo;
  onLoadSellerInfo?: (sellerInfo: SellerInfo) => any;
};

const EntitiesForm: React.FC<Props & { formik: FormikContextType<any> }> = ({
  data,
  gridClass,
  isModalDialog,
  showButtons = true,
  handleBack,
  formik,
  sellerInfo,
  onLoadSellerInfo,
}) => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const [localSelectedEntity, setLocalSelectedEntity] = useState<
    Nullable<Entity>
  >();
  const { values, setFieldValue } = formik;

  // set entity by formik value id
  useEffect(() => {
    const entity = data.find(({ id }) => id === formik.values.entity);
    setLocalSelectedEntity(entity);
  }, [data, formik.values.entity]);

  useEffect(() => {
    setFieldValue('dni', '');
  }, [setFieldValue, values.entity]);

  useEffect(() => {
    if (sellerInfo && formik.isSubmitting) {
      dispatch(AuthActions.setAgentInfo(sellerInfo));
    }
  }, [dispatch, sellerInfo, formik.isSubmitting]);

  const setIconEntity = useCallback((value: string) => {
    return EntityIconLabel({ business: value });
  }, []);

  return (
    <Form id="form-id">
      <Grid container className={gridClass}>
        <Field id="entity" name="entity">
          {(formikProps: FieldProps) => (
            <Select
              {...formikProps}
              label={dictionary.header_entitiesForm_label}
              inputProps={{
                'aria-label': dictionary.header_entitiesForm_label,
              }}
              items={data}
              bindValue="id"
              bindLabel={({ id, commercialName }: Entity) =>
                `ID ${id} - ${commercialName}`
              }
              showIcons
              setIcons={setIconEntity}
              iconKey={'business'}
              fullWidth
            />
          )}
        </Field>
        {localSelectedEntity?.channelType === ChannelType.Indirect && (
          <Field
            id="dni"
            name="dni"
            className={
              isModalDialog ? classes.docNumberDialog : classes.docNumber
            }
            entityId={formik.values.entity}
            selectedEntity={localSelectedEntity?.id}
            inputProps={{
              autoComplete: 'off',
              'data-testid': 'documentNumber-id',
            }}
            onLoadData={onLoadSellerInfo}
            component={AgentDocumentNumberField}
          />
        )}

        {showButtons && (
          <GridBox container spacing={2} mt={11}>
            <Grid item>
              <GoBackButton onClick={handleBack} />
            </Grid>
            <Grid item>
              <SubmitButton data-testid="btn-submit">
                {dictionary.header_entitiesForm_submitBtn}
              </SubmitButton>
            </Grid>
          </GridBox>
        )}
      </Grid>
    </Form>
  );
};

export default connect<any, any>(EntitiesForm);
